import "./ExpandableText.scss";

import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";

// The div version of the editable text field has padding that
// makes the clientHeight smaller than the scrollHeight.
// This constant is used to adjust the height calculation.
// As this value is currently less than line-height, it should
// not adversely affect the height calculation.  If it does
// get larger, however, we probably will need to pass in the
// padding offset we expect.
const DIV_PADDING_OFFSET = 12;

type ExpandableTextProps = {
  children: ReactNode | ReactNode[];
  className?: string;
  lines?: number;
  showExpanded?: boolean;
};

export const ExpandableText: FC<ExpandableTextProps> = ({
  children,
  className,
  lines = 3,
  showExpanded,
}) => {
  const [isExpanded, setIsExpanded] = useState(showExpanded);
  const [showReadMore, setShowReadMore] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showExpanded && textRef.current) {
      textRef.current.scrollTop = 0;
    }
  }, [showExpanded]);

  useEffect(() => {
    if (!textRef.current) return;

    const checkOverflow = () => {
      const el = textRef.current;
      if (el) {
        setShowReadMore(el.scrollHeight > el.clientHeight + DIV_PADDING_OFFSET);
      }
    };

    const observer = new ResizeObserver(() => {
      checkOverflow();
    });

    observer.observe(textRef.current);

    // Initial check (in case ResizeObserver doesn't fire immediately)
    textRef.current.scrollTop = 0;
    checkOverflow();

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setIsExpanded(showExpanded);
  }, [showExpanded]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const maxHeight = `${lines * 1.5}em`;
  const readText = isExpanded ? t("expandable_text.read_less") : t("expandable_text.read_more");

  return (
    <div
      className={twMerge("expandable-text-container", className, isExpanded && "expanded")}
      ref={textRef}
      style={{ maxHeight: isExpanded ? "none" : maxHeight }}
    >
      {children}
      {showReadMore && !showExpanded && (
        <div className="read-more" onClick={toggleExpanded}>
          {readText}
        </div>
      )}
    </div>
  );
};
